import React, { useState } from 'react';
import { toast } from 'sonner';
import { Avatar, Badge, BmModal, Btn, Checkbox, ErrorState, Field, Input } from '../../ForBrands/ui/BrandUI';
import { Icon } from '../../ForBrands/ui/icons';
import { BASE_URL } from '../../../constants';
import { calculateUserRole } from '../../../utils/utils';
import { sentence } from '../../auth/v2/serverText';

// "Transfer account" on the Users page. Our team builds some athlete
// profiles itself, signed in as admin+<name>@cachescore.com, to show coaches
// and athletes. Transferring one gives the account the athlete's own email:
// the server (POST admin/users/<email>/transfer/) moves the profile, ends the
// team's login and sessions, and answers a claim link. That is a password
// reset link the athlete opens to choose a password (the reset page words
// it as a welcome for &claim=1); Continue with Google with that email claims
// the account too. Until one of them happens the account carries
// transfer.status "pending", and admin/users/<email>/transfer/link/ gives a
// fresh claim link, and the claim email again when asked.

export const INVALID_EMAIL = 'Enter a valid email.';
export const EMAILS_DIFFER = "The emails don't match.";
export const TEAM_EMAIL = "Use the athlete's own email, not a cachescore.com address.";
export const SAME_EMAIL = 'This account already uses this email.';
export const TRANSFER_FAILED = 'The transfer did not go through. Try again.';
export const CLAIM_LINK_FAILED = 'Could not get a claim link. Try again.';
export const LINK_COPIED = 'Claim link copied.';
export const COPY_FAILED = 'Could not copy. Select the link and copy it.';
export const EMAIL_SENT_LINE = 'We emailed them a link to set their password.';
export const EMAIL_FAILED_LINE = 'The email did not send. Copy the link and share it with them.';
export const EMAIL_SKIPPED_LINE = 'We did not email them. Copy the link and share it with them.';
export const LINK_LIFETIME = 'The link works for 24 hours. After that, they can use Forgot password on the login page with this email.';
export const TRANSFER_EXPLAINER = "The profile moves to this email and our team's login stops working. The athlete sets a password from the email, or uses Continue with Google with this address.";
export const PILOT_EXPLAINER = "This pilot profile becomes a live account and keeps everything on it: stats, teams and team history. It can keep its current email or move to the athlete's or a parent's. They set a password from the email, or use Continue with Google with that address.";
export const PILOT_DONE_LINE = 'The pilot profile is now a live account, with all its data.';
export const MERGE_PILOT_LABEL = 'Merge the imported profile into this account';

// The server's sentences for its refusals, for a reply that carries only
// the code.
const REFUSALS = {
  invalid_email: INVALID_EMAIL,
  team_email: TEAM_EMAIL,
  same_email: SAME_EMAIL,
  account_not_found: 'This account was not found. Refresh the list and try again.',
  not_transferable: 'Only athlete accounts our team created or imported can be transferred.',
  email_in_use: 'Another account already uses this email.',
  not_pending: 'This account has no transfer waiting to be claimed.',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const domainOf = (email) => email.slice(email.lastIndexOf('@') + 1);

// A cachescore.com address (ours, any subdomain too) or one of the made-up
// addresses archived and imported records carry (merged-<id>@archived.cache).
export const isTeamOrSyntheticEmail = (value) => {
  const domain = domainOf(normalizeEmail(value));
  return domain === 'cachescore.com' || domain.endsWith('.cachescore.com') || domain.endsWith('.cache');
};

// Why the two typed emails cannot be sent yet, or '' when they can. The
// server checks the same and more (another account with the email). A pilot
// profile may keep its own email (`allowSame`).
export const transferInputProblem = (email, again, currentEmail, allowSame = false) => {
  const next = normalizeEmail(email);
  if (!EMAIL_RE.test(next)) return INVALID_EMAIL;
  if (next !== normalizeEmail(again)) return EMAILS_DIFFER;
  if (isTeamOrSyntheticEmail(next)) return TEAM_EMAIL;
  if (!allowSame && next === normalizeEmail(currentEmail)) return SAME_EMAIL;
  return '';
};

// Roles that make an account more than an athlete's. The server transfers
// none of them.
const OTHER_ROLES = ['IS_ADMIN', 'IS_CACHE_AI', 'IS_INTERN', 'IS_COACH', 'IS_BRAND_AGENCY', 'IS_RECRUITER_SCOUT', 'IS_AD_PD', 'IS_REP'];

export const transferPending = (row) => row?.transfer?.status === 'pending';

// An imported (pilot) profile: the transfer also makes it a live account.
export const isPilotRow = (row) => String(row?.cohort || '').toLowerCase() === 'pilot';

// Whether "Transfer account" is offered for a row, as the server decides
// it: an athlete account and nothing more, not archived or merged, that is
// either live and signed in with a cachescore.com email, live with a
// transfer still waiting (an admin can move it again after a typo), or an
// imported pilot profile (any email: the transfer activates it). A locked
// one is offered too, held until it is unlocked, since the server refuses
// it until then.
export const canTransfer = (row) => {
  const perms = row?.permissions || [];
  if (!perms.includes('IS_ATHLETE') || OTHER_ROLES.some((p) => perms.includes(p))) return false;
  const email = normalizeEmail(row.email);
  const archived = String(row.cohort || '').toLowerCase() === 'archived'
    || !!(row.mergedInto || row.athlete?.mergedInto)
    || email.endsWith('@archived.cache');
  if (archived) return false;
  if (isPilotRow(row)) return true;
  if (String(row.cohort || '').toLowerCase() !== 'live') return false;
  return email.endsWith('@cachescore.com') || transferPending(row);
};

const text = (v) => (typeof v === 'string' ? v.trim() : '');

// The words to show for a refused transfer or claim link: the server's
// `message`, else the sentence for its code, else an `error` or `detail`
// sentence (never a bare code), else a plain line for the status.
export const transferErrorMessage = (status, data, fallback = TRANSFER_FAILED) => {
  const body = data && typeof data === 'object' ? data : {};
  if (text(body.message)) return text(body.message);
  if (body.error === 'email_in_use_pilot') {
    const name = text(body.pilot?.name);
    return `This email belongs to an imported profile${name ? ` of ${name}` : ''}. Merge it into this account to transfer.`;
  }
  if (typeof body.error === 'string' && Object.prototype.hasOwnProperty.call(REFUSALS, body.error)) {
    return REFUSALS[body.error];
  }
  const said = sentence(body.error) || sentence(body.detail);
  if (said) return said;
  if (status === 403) return 'Only admins can transfer accounts.';
  if (status === 404) return REFUSALS.account_not_found;
  return fallback;
};

const COHORT_LABEL = { live: 'Live', pilot: 'Pilot', archived: 'Archived' };

// "Name · Role · Cohort" for the account that already holds an email.
export const accountLine = (account) => {
  if (!account || typeof account !== 'object') return '';
  const role = text(account.role);
  const roleName = /^IS_[A-Z_]+$/.test(role) ? (calculateUserRole([role]) || role) : role;
  const cohort = text(account.cohort).toLowerCase();
  return [text(account.name), roleName, COHORT_LABEL[cohort] || cohort].filter(Boolean).join(' · ');
};

const transferUrl = (email) => `${BASE_URL}/api/users/admin/users/${encodeURIComponent(email)}/transfer/`;

// A POST as the Users page sends them (the admin's token from storage),
// answering {ok, status, data}; data is {} when the reply is not JSON.
// A network failure throws.
const post = async (url, body) => {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    body: JSON.stringify(body),
  });
  let data = {};
  try {
    data = (await res.json()) || {};
  } catch (e) {
    data = {};
  }
  return { ok: res.ok, status: res.status, data };
};

// {newEmail, mergePilot, sendEmail}; answers {status, oldEmail, newEmail,
// emailSent, claimLink, mergedPilot, transfer}.
export const transferAccount = (email, body) => post(transferUrl(email), body);

// For a transfer still waiting: a fresh claim link, and the claim email
// again when `sendEmail`; answers {claimLink, emailSent, transfer}.
export const requestClaimLink = (email, sendEmail) => post(`${transferUrl(email)}link/`, { sendEmail: !!sendEmail });

// True once the text is on the clipboard. A browser that refuses (Safari
// after a request, a page without focus) answers false.
export const copyText = async (value) => {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(value);
    return true;
  } catch (e) {
    return false;
  }
};

export const copyClaimLink = async (link) => {
  if (await copyText(link)) toast.success(LINK_COPIED);
  else toast.error(COPY_FAILED);
};

// The row badge for a transfer the athlete has not claimed yet.
export const TransferPendingBadge = ({ row }) => {
  if (!transferPending(row)) return null;
  const to = row.transfer?.to || row.email;
  return (
    <span title={`Transferred to ${to}. Waiting for them to set a password or use Continue with Google.`}>
      <Badge tone="attention">Transfer pending</Badge>
    </span>
  );
};

// The claim link in full, selectable, for copying by hand.
export const ClaimLinkBox = ({ link }) => (
  <div className="rounded-[8px] border border-bm-border bg-bm-subtle px-3 py-2">
    <code data-testid="claim-link" title={link} className="block select-all truncate font-mono bm-body-s text-bm-ink">{link}</code>
  </div>
);

// A claim link on screen: when the clipboard refused it, or when the claim
// email did not send (`emailFailed`).
export const ClaimLinkDialog = ({ email, link, emailFailed = false, onClose }) => (
  <BmModal
    title="Claim link"
    onClose={onClose}
    width={480}
    footer={(
      <>
        <Btn variant="secondary" onClick={() => copyClaimLink(link)}>
          <Icon name="copy" size={14} className="mr-1.5" /> Copy claim link
        </Btn>
        <Btn onClick={onClose}>Done</Btn>
      </>
    )}
  >
    <div className="space-y-3">
      <p className="bm-body-m text-bm-ink">
        {emailFailed ? EMAIL_FAILED_LINE : `Copy this link and share it with ${email}.`}
      </p>
      <ClaimLinkBox link={link} />
      <p className="bm-body-s text-bm-muted">{LINK_LIFETIME}</p>
    </div>
  </BmModal>
);

const displayName = (user) => (
  user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim()
  || [user.athlete?.name, user.athlete?.surname].filter(Boolean).join(' ') || user.email
);

// The transfer form, then its result. `onTransferred` gets the server's
// answer as soon as the transfer is through, so the list can refresh behind
// the result.
const TransferAccountDialog = ({ user, onClose, onTransferred }) => {
  const fromPilot = isPilotRow(user);
  // A pilot profile usually carries the athlete's real email already: it
  // starts filled in, ready to keep or change.
  const keptEmail = fromPilot && !isTeamOrSyntheticEmail(user?.email) ? normalizeEmail(user?.email) : '';
  const [email, setEmail] = useState(keptEmail);
  const [again, setAgain] = useState(keptEmail);
  const [sendEmail, setSendEmail] = useState(true);
  // The 409 email_in_use_pilot answer, for the email it was given for.
  const [pilot, setPilot] = useState(null);
  const [mergePilot, setMergePilot] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!user) return null;
  const name = displayName(user);

  const close = () => {
    if (!busy) onClose();
  };

  const editEmail = (value) => {
    setEmail(value);
    setError(null);
    // The merge offer was for the email typed then.
    if (pilot && normalizeEmail(value) !== pilot.email) {
      setPilot(null);
      setMergePilot(false);
    }
  };

  const editAgain = (value) => {
    setAgain(value);
    setError(null);
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (busy) return;
    const problem = transferInputProblem(email, again, user.email, fromPilot);
    if (problem) {
      setError({ message: problem });
      return;
    }
    if (pilot && !mergePilot) return;
    const newEmail = normalizeEmail(email);
    setBusy(true);
    setError(null);
    let reply;
    try {
      reply = await transferAccount(user.email, { newEmail, mergePilot: !!(pilot && mergePilot), sendEmail });
    } catch (err) {
      setBusy(false);
      setError({ message: TRANSFER_FAILED });
      return;
    }
    const { ok, status, data } = reply;
    setBusy(false);
    if (ok) {
      setResult({ ...data, newEmail: data.newEmail || newEmail, emailAsked: sendEmail });
      if (onTransferred) onTransferred(data);
      return;
    }
    if (status === 409 && data.error === 'email_in_use_pilot') {
      setPilot({ email: newEmail, message: transferErrorMessage(status, data) });
      setMergePilot(false);
      return;
    }
    setError({ message: transferErrorMessage(status, data), account: data.error === 'email_in_use' ? accountLine(data.account) : '' });
  };

  if (result) {
    let emailLine = EMAIL_SENT_LINE;
    if (!result.emailSent) emailLine = result.emailAsked ? EMAIL_FAILED_LINE : EMAIL_SKIPPED_LINE;
    return (
      <BmModal
        title="Transfer to the athlete"
        onClose={onClose}
        width={520}
        footer={(
          <>
            {result.claimLink && (
              <Btn variant="secondary" onClick={() => copyClaimLink(result.claimLink)}>
                <Icon name="copy" size={14} className="mr-1.5" /> Copy claim link
              </Btn>
            )}
            <Btn onClick={onClose}>Done</Btn>
          </>
        )}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Icon name="check-circle" size={20} className="mt-[2px] text-bm-orange" />
            <div className="min-w-0 space-y-1">
              <p className="break-words bm-label-l text-bm-ink">{`Transferred to ${result.newEmail}.`}</p>
              <p className="bm-body-m text-bm-muted">{emailLine}</p>
              {result.activatedPilot && <p className="bm-body-m text-bm-muted">{PILOT_DONE_LINE}</p>}
              {result.mergedPilot && <p className="bm-body-m text-bm-muted">The imported profile was merged into this account.</p>}
            </div>
          </div>
          {/* A step that did not finish after the account moved (the backup
              records it, so it can be put right by hand). */}
          {Array.isArray(result.warnings) && result.warnings.length > 0 && (
            <div role="status" className="rounded-[8px] bg-bm-attention/15 px-3 py-2">
              <p className="bm-label-m text-bm-ink">A few things to check:</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {result.warnings.map((w) => <li key={w} className="bm-body-s text-bm-ink">{w}</li>)}
              </ul>
            </div>
          )}
          {result.claimLink && (
            <>
              <ClaimLinkBox link={result.claimLink} />
              <p className="bm-body-s text-bm-muted">{LINK_LIFETIME}</p>
            </>
          )}
        </div>
      </BmModal>
    );
  }

  return (
    <BmModal
      title="Transfer to the athlete"
      onClose={close}
      width={520}
      dismissible={!busy}
      footer={(
        <>
          <Btn variant="secondary" onClick={close} disabled={busy}>Cancel</Btn>
          <Btn onClick={submit} disabled={busy || (!!pilot && !mergePilot)}>
            {busy ? 'Transferring...' : fromPilot ? 'Activate and transfer' : 'Transfer account'}
          </Btn>
        </>
      )}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <div className="flex items-center gap-3 rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-3">
          <Avatar name={name} src={user.athlete?.avatar || ''} size={36} />
          <div className="min-w-0 flex-1">
            <div className="truncate bm-label-l text-bm-ink">{name}</div>
            <div className="truncate bm-body-s text-bm-muted" title={user.email}>{user.email}</div>
          </div>
          <TransferPendingBadge row={user} />
        </div>
        <Field label="Athlete's email">
          <Input
            type="email"
            autoFocus
            autoComplete="off"
            value={email}
            onChange={(e) => editEmail(e.target.value)}
            placeholder="name@email.com"
          />
        </Field>
        <Field label="Type it again">
          <Input
            type="email"
            autoComplete="off"
            value={again}
            onChange={(e) => editAgain(e.target.value)}
            placeholder="name@email.com"
          />
        </Field>
        <Checkbox checked={sendEmail} onChange={setSendEmail} label="Email them a link to set their password" />
        <p className="bm-body-s text-bm-muted">{fromPilot ? PILOT_EXPLAINER : TRANSFER_EXPLAINER}</p>
        {pilot && (
          <div className="space-y-3 rounded-[8px] bg-bm-attention/15 px-4 py-3">
            <p role="status" className="bm-body-m text-bm-ink">{pilot.message}</p>
            <Checkbox checked={mergePilot} onChange={setMergePilot} label={MERGE_PILOT_LABEL} />
          </div>
        )}
        {error && (
          <ErrorState role="alert">
            {error.message}
            {error.account && <span className="mt-1 block bm-body-s">{`Account: ${error.account}`}</span>}
          </ErrorState>
        )}
      </form>
    </BmModal>
  );
};

export default TransferAccountDialog;
